import { useMemo, useState } from 'react';
import { BsChevronContract as CollapseIcon, BsChevronExpand as ExpandIcon } from 'react-icons/bs';
import { Button, RichTextRenderer } from '@knack/asterisk-react';
import truncate from 'lodash/truncate';

import { type KnackField } from '@/types/schema/KnackField';
import { cn } from '@/utils/tailwind';

export function DefaultRecordCellContent({
  value,
  field,
  isCharacterLimitExceeded,
  characterLimit,
  connSeparator,
  connectedFieldKey
}: {
  value: string;
  field: KnackField | undefined;
  isCharacterLimitExceeded: boolean;
  characterLimit?: number;
  connSeparator?: string;
  connectedFieldKey?: string;
}) {
  const [isContentExpanded, setIsContentExpanded] = useState(false);

  const CollapsibleIcon = isContentExpanded ? CollapseIcon : ExpandIcon;

  const toogleContentExpansion = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Stop propagation to avoid triggering inline editing when clicking the button
    e.stopPropagation();
    setIsContentExpanded(!isContentExpanded);
  };

  // Format value based on connSeparator for different field types:
  // - multiple_choice fields: Convert comma-separated values to new line format when needed
  // - connected fields: Convert between comma-separated and new line formats based on separator
  const formattedValue = useMemo(() => {
    // Handle multiple choice field formatting
    if (
      field?.type === 'multiple_choice' &&
      (connSeparator === 'new_line' || connSeparator === '')
    ) {
      return value.replace(/, /g, '<br>');
    }

    // Handle connected field formatting
    if (connectedFieldKey) {
      if (connSeparator === 'new_line' || connSeparator === '') {
        return value.replace(/>, </g, '><br><');
      }

      if (connSeparator === 'comma') {
        return value.replace(/><br><|><br> </g, '>, <');
      }
    }

    // Return original value if no formatting is needed
    return value;
  }, [connectedFieldKey, connSeparator, field?.type, value]);

  return (
    <div>
      <RichTextRenderer
        className={cn({
          'mr-6': isCharacterLimitExceeded
        })}
        dangerouslySetInnerHTML={{
          __html:
            isCharacterLimitExceeded && !isContentExpanded
              ? // We add +3 to take into account the 3 ellipsis points added by the truncate function
                truncate(formattedValue, { length: Number(characterLimit) + 3 })
              : formattedValue
        }}
      />
      {isCharacterLimitExceeded && (
        <Button
          data-testid="cell-content-expand-button"
          className="absolute bottom-0 right-0 size-4"
          size="xs"
          intent="minimalStandalone"
          onClick={toogleContentExpansion}
        >
          <CollapsibleIcon size={16} />
        </Button>
      )}
    </div>
  );
}
